"use client";

import React from "react";
import { PhoneCall, SearchCheck, Compass, Zap, TrendingUp, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const processSteps = [{
  step: "01",
  title: "Free SEO Consultation",
  description: "I conduct a free initial consultation to understand your business model, target audience, commercial priorities, and current organic performance bottlenecks.",
  deliverable: "30-minute discovery call & initial scope",
  icon: PhoneCall
}, {
  step: "02",
  title: "SEO Assessment & Audit",
  description: "I perform an exhaustive technical, on-page, and architectural audit of your website to identify indexing issues, code errors, content gaps, and ranking barriers.",
  deliverable: "Detailed audit document & action matrix",
  icon: SearchCheck
}, {
  step: "03",
  title: "SEO Strategy & Priorities",
  description: "I build a tailored roadmap sequenced by commercial impact and ease of implementation, outlining keyword targets, content briefs, and technical fixes.",
  deliverable: "Sequenced 3-to-6 month growth roadmap",
  icon: Compass
}, {
  step: "04",
  title: "Implementation & Optimisation",
  description: "I either implement the recommendations directly or collaborate closely with your development and editorial teams to ensure every optimization is executed accurately.",
  deliverable: "Hands-on implementation & dev guidance",
  icon: Zap
}, {
  step: "05",
  title: "Measurement & Ongoing Improvement",
  description: "I track keyword positions, search visibility, organic conversions, and AI search visibility, refining our approach monthly based on verified analytics data.",
  deliverable: "Monthly transparent reporting & review",
  icon: TrendingUp
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="howprocessworks" data-nav="light" className="relative bg-background py-20 md:py-28 lg:py-32 border-t border-border overflow-hidden">
      {/* Subtle decorative background tint */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-1/4 size-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 left-1/4 size-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 px-3 py-1">
            Structured, Hands-On Process
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif text-foreground tracking-tight mb-5">
            How the Process Works
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-sans">
            I lead and execute each phase hands-on, tailoring technical recommendations and search strategy to your specific commercial goals.
          </p>
        </div>

        {/* Process Flow */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {processSteps.map((item, i) => {
          const IconComponent = item.icon;
          const isLast = i === processSteps.length - 1;
          return <motion.div key={item.step} data-index={i} variants={itemVariants} className={`relative flex flex-col justify-between rounded-xl bg-card border border-border p-7 sm:p-8 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md ${isLast ? "md:col-span-2 lg:col-span-1" : ""}`}>
                <div>
                  {/* Step Header: Number and Icon */}
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <span className="text-3xl font-extrabold font-serif text-primary tracking-tight">
                      {item.step}
                    </span>
                    <div className="flex items-center justify-center size-12 rounded-lg bg-primary/10 text-primary border border-primary/20">
                      <IconComponent className="size-6" />
                    </div>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold font-serif text-card-foreground mb-3 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans mb-6">
                    {item.description}
                  </p>
                </div>

                {/* Deliverable Badge / Note */}
                <div className="pt-4 border-t border-border flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-xs font-medium text-foreground">
                    {item.deliverable}
                  </span>
                </div>
              </motion.div>;
        })}

          {/* Quick CTA Card to fill grid balance on desktop */}
          <motion.div variants={itemVariants} className="flex flex-col justify-between rounded-xl bg-primary text-primary-foreground p-7 sm:p-8 shadow-md">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider bg-white/15 text-primary-foreground px-2.5 py-1 rounded-md inline-block mb-6">
                Start Today
              </span>
              <h3 className="text-2xl font-bold font-serif mb-3 leading-snug">
                Ready to improve your search visibility?
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/90 leading-relaxed font-sans mb-6">
                Book a direct, no-obligation discovery call to discuss your website
                goals and find out how we can work together.
              </p>
            </div>

            <div>
              <Button asChild size="lg" variant="secondary" className="w-full bg-white text-foreground hover:bg-white/90 font-semibold shadow-xs">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Trust Guarantee Strip */}
        <div className="mt-16 text-center border-t border-border pt-10">
          <p className="text-sm font-medium text-muted-foreground">
            Defined deliverables, direct technical communication, and flexible month-to-month terms.
          </p>
        </div>
      </div>
    </section>;
}